import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserCircle2 } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

export default function ProfilePage() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone_ch: "", phone_eg: "", company: "",
        brand_name: "", brand_subtitle: "", brand_logo_url: "",
        address_street: "", address_zip: "", address_city: "", address_country: "Schweiz",
        iban: "", bank_name: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user && user !== false) {
            setForm({
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                email: user.email || "",
                phone_ch: user.phone_ch || "",
                phone_eg: user.phone_eg || "",
                company: user.company || "",
                brand_name: user.brand_name || "",
                brand_subtitle: user.brand_subtitle || "",
                brand_logo_url: user.brand_logo_url || "",
                address_street: user.address_street || "",
                address_zip: user.address_zip || "",
                address_city: user.address_city || "",
                address_country: user.address_country || "Schweiz",
                iban: user.iban || "",
                bank_name: user.bank_name || "",
            });
        }
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/auth/me", form);
            await refresh();
            toast.success("Profil gespeichert.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <AppLayout>
            <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.18em] text-ink-tertiary mb-3">Konto</p>
                <h1 className="text-4xl font-bold mb-2 text-ink" style={{ fontFamily: "Space Grotesk" }}>
                    Dein <span className="italic font-normal" style={{ fontFamily: "Fraunces" }}>Profil</span>
                </h1>
                <p className="text-sm text-ink-secondary mb-8">
                    Diese Angaben erscheinen in der Begrüssung im Dashboard und können später automatisch in PDFs übernommen werden.
                </p>

                <form onSubmit={submit} className="pt-card p-8 space-y-5" data-testid="profile-form">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-navy text-sand flex items-center justify-center">
                            <UserCircle2 className="w-7 h-7" strokeWidth={1.5} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-ink">{user?.name || "—"}</p>
                            <p className="text-xs text-ink-tertiary">{user?.email}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Vorname" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="profile-first-name" />
                        <Field label="Nachname" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="profile-last-name" />
                    </div>
                    <Field label="E-Mail" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="profile-email" />
                    <Field label="Firma (optional)" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="profile-company" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Telefon CH" type="tel" value={form.phone_ch} onChange={(v) => setForm({ ...form, phone_ch: v })} placeholder="+41 79 ..." testid="profile-phone-ch" />
                        <Field label="Telefon EG" type="tel" value={form.phone_eg} onChange={(v) => setForm({ ...form, phone_eg: v })} placeholder="+20 100 ..." testid="profile-phone-eg" />
                    </div>

                    <div className="pt-5 mt-2 border-t border-sand-300">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-tertiary mb-1">Branding</p>
                        <h3 className="text-base font-semibold text-ink mb-1">Tool-Name in der Seitenleiste</h3>
                        <p className="text-xs text-ink-tertiary mb-4">
                            Erscheint oben links in der Navigation. Leer lassen für Standard ("Pro-Travel" / "Planner").
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field
                                label="Tool-Name"
                                value={form.brand_name}
                                onChange={(v) => setForm({ ...form, brand_name: v })}
                                placeholder="Pro-Travel"
                                testid="profile-brand-name"
                            />
                            <Field
                                label="Untertitel"
                                value={form.brand_subtitle}
                                onChange={(v) => setForm({ ...form, brand_subtitle: v })}
                                placeholder="Planner"
                                testid="profile-brand-subtitle"
                            />
                        </div>

                        <div className="mt-4">
                            <ImageUploader
                                label="Logo (optional, ersetzt das Kompass-Icon)"
                                value={form.brand_logo_url}
                                onChange={(v) => setForm({ ...form, brand_logo_url: v })}
                                testid="profile-brand-logo"
                            />
                            <p className="text-[10px] text-ink-tertiary mt-2">
                                Quadratisches Bild empfohlen (z.B. 256×256 px). Wird in der Sidebar als kleines Icon angezeigt.
                            </p>
                        </div>
                    </div>

                    <div className="pt-5 mt-2 border-t border-sand-300">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-tertiary mb-1">Rechnungsdaten</p>
                        <h3 className="text-base font-semibold text-ink mb-1">Adresse & Bankverbindung</h3>
                        <p className="text-xs text-ink-tertiary mb-4">
                            Erscheinen auf der Rechnungs-PDF. Leer lassen wenn nicht relevant.
                        </p>
                        <Field label="Strasse & Nr." value={form.address_street} onChange={(v) => setForm({ ...form, address_street: v })} placeholder="Bahnhofstrasse 12" testid="profile-address-street" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Field label="PLZ" value={form.address_zip} onChange={(v) => setForm({ ...form, address_zip: v })} placeholder="8001" testid="profile-address-zip" />
                            <Field label="Ort" value={form.address_city} onChange={(v) => setForm({ ...form, address_city: v })} placeholder="Zürich" testid="profile-address-city" />
                            <Field label="Land" value={form.address_country} onChange={(v) => setForm({ ...form, address_country: v })} testid="profile-address-country" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <Field label="Bank" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} placeholder="UBS Schweiz AG" testid="profile-bank-name" />
                            <Field label="IBAN" value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} placeholder="CH93 0076 ..." testid="profile-iban" />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-sand-300">
                        <button type="submit" disabled={saving} data-testid="profile-save"
                            className="bg-navy hover:bg-navy-hover text-sand rounded-md px-6 py-2.5 font-medium shadow-card disabled:opacity-50">
                            {saving ? "Wird gespeichert ..." : "Speichern"}
                        </button>
                    </div>
                </form>
            </div>
        </AppLayout>
    );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-navy"
            />
        </div>
    );
}
